import { forwardRef, memo } from 'react'
import { useStableCallbacks } from '@/hooks/use-stable-callbacks'
import { SelectionOutlineHighlights } from './components/selection-outline-highlights'
import { ShapeLayerItem } from './components/shape-layer-item'
import { useAltClickCycle } from './hooks/use-alt-click-cycle'
import { useAltDragDuplicate } from './hooks/use-alt-drag-duplicate'
import { useChainTextClipboard } from './hooks/use-chain-text-clipboard'
import { useChainTextSelection } from './hooks/use-chain-text-selection'
import { useEditScopeRefs } from './hooks/use-edit-scope-refs'
import { useFeedbackPulses } from './hooks/use-feedback-pulses'
import { useGestureCommit } from './hooks/use-gesture-commit'
import { useGesturePointerBridge } from './hooks/use-gesture-pointer-bridge'
import { useGestureSnapping } from './hooks/use-gesture-snapping'
import { useGestureStart } from './hooks/use-gesture-start'
import { useInteractionControl } from './hooks/use-interaction-control'
import { useLiveGestureUpdates } from './hooks/use-live-gesture-updates'
import { useMultiSelectChromeCommands } from './hooks/use-multi-select-chrome-commands'
import { useMultiSelectChromeSync } from './hooks/use-multi-select-chrome-sync'
import { usePanelMultiSelectRotation } from './hooks/use-panel-multi-select-rotation'
import { useSelectionBoxPointerHandlers } from './hooks/use-selection-box-pointer-handlers'
import { useSelectionHistory } from './hooks/use-selection-history'
import { useSelectionNotify } from './hooks/use-selection-notify'
import { useSelectionPresentation } from './hooks/use-selection-presentation'
import { useShapeBounds } from './hooks/use-shape-bounds'
import { useShapeClickHandlers } from './hooks/use-shape-click-handlers'
import { useShapeCollection } from './hooks/use-shape-collection'
import { useShapeGrouping } from './hooks/use-shape-grouping'
import { useShapeHitTesting } from './hooks/use-shape-hit-testing'
import { useShapeLayering } from './hooks/use-shape-layering'
import { useShapeLockFreeze } from './hooks/use-shape-lock-freeze'
import { useShapeManagerHandle } from './hooks/use-shape-manager-handle'
import { useShapeManagerState } from './hooks/use-shape-manager-state'
import { useShapeRenderModel } from './hooks/use-shape-render-model'
import { useShapeSelection } from './hooks/use-shape-selection'
import { useShapeTextCommit } from './hooks/use-shape-text-commit'
import { useShapeTextEditing } from './hooks/use-shape-text-editing'
import { useShapeTextReplace } from './hooks/use-shape-text-replace'
import { useShapeTextWrapContext } from './hooks/use-shape-text-wrap-context'
import { useShapeUngrouping } from './hooks/use-shape-ungrouping'
import { useShapeUpdates } from './hooks/use-shape-updates'
import { useSmartGuides } from './hooks/use-smart-guides'
import { useSnapRefs } from './hooks/use-snap-refs'
import {
  type ShapeLayerRenderState,
  shapeLayerPresentation,
  shapeLayerText,
} from './helpers/shape-layer-item-inputs'
import type { ShapeLayerHandlers } from './types/shape-layer-item-props'
import type { ShapeManagerProps } from './types/shape-manager-props'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'

/**
 * The canvas's shape layer: owns every shape's state, selection, gestures and embedded text editing,
 * and exposes them through `ShapeManagerRef`.
 *
 * Each hook below receives everything the hooks before it produced. They run in this fixed order —
 * the order the code originally had in one component — because React runs effects in call order and
 * several layout effects depend on it (chrome sync before selection notify, and so on).
 */
const ShapeManagerInner = forwardRef<ShapeManagerRef, ShapeManagerProps>((props, ref) => {
  const state = useShapeManagerState(props)
  const c1 = { ...state, ...useShapeTextWrapContext(state) }
  const c2 = { ...c1, ...useShapeBounds(c1) }
  const c3 = { ...c2, ...useSelectionHistory(c2) }
  const c4 = { ...c3, ...useMultiSelectChromeSync(c3) }
  const c5 = { ...c4, ...useEditScopeRefs(c4) }
  const c6 = { ...c5, ...useSnapRefs(c5) }
  const c7 = { ...c6, ...useSelectionNotify(c6) }
  const c8 = { ...c7, ...useShapeCollection(c7) }
  const c9 = { ...c8, ...useShapeLockFreeze(c8) }
  const c10 = { ...c9, ...useShapeUpdates(c9) }
  const c11 = { ...c10, ...useSelectionPresentation(c10) }
  const c12 = { ...c11, ...useFeedbackPulses(c11) }
  const c13 = { ...c12, ...useShapeSelection(c12) }
  const c14 = { ...c13, ...useShapeLayering(c13) }
  const c15 = { ...c14, ...useShapeGrouping(c14) }
  const c16 = { ...c15, ...useShapeUngrouping(c15) }
  const c17 = { ...c16, ...useSmartGuides(c16) }
  const c18 = { ...c17, ...usePanelMultiSelectRotation(c17) }
  const c19 = { ...c18, ...useMultiSelectChromeCommands(c18) }
  const c20 = { ...c19, ...useShapeTextCommit(c19) }
  const c21 = { ...c20, ...useChainTextSelection(c20) }
  const c22 = { ...c21, ...useChainTextClipboard(c21) }
  const c23 = { ...c22, ...useShapeTextEditing(c22) }
  const c24 = { ...c23, ...useInteractionControl(c23) }
  const c25 = { ...c24, ...useGestureSnapping(c24) }
  const c26 = { ...c25, ...useLiveGestureUpdates(c25) }
  const c27 = { ...c26, ...useGestureCommit(c26) }

  useGesturePointerBridge(c27)
  const c28 = { ...c27, ...useAltDragDuplicate(c27) }
  const c29 = { ...c28, ...useGestureStart(c28) }
  const c30 = { ...c29, ...useShapeHitTesting(c29) }
  const c31 = { ...c30, ...useAltClickCycle(c30) }
  const c32 = { ...c31, ...useShapeClickHandlers(c31) }
  const c33 = { ...c32, ...useSelectionBoxPointerHandlers(c32) }
  const c34 = { ...c33, ...useShapeTextReplace(c33) }
  const ctx = { ...c34, ...useShapeRenderModel(c34) }

  useShapeManagerHandle(ref, ctx)

  const layerState: ShapeLayerRenderState = {
    shapesById: ctx.shapesById,
    selectedIdSet: ctx.selectedIdSet,
    selectedShapeIds: ctx.selectedShapeIds,
    selectedShape: ctx.selectedShape,
    isSingleSelect: ctx.isSingleSelect,
    isResizeGestureActive: ctx.isResizeGestureActive,
    altHoveredShapeId: ctx.altHoveredShapeId,
    frozenOutlinePulse: ctx.frozenOutlinePulse,
    activeGroupIsolationId: ctx.activeGroupIsolationId,
    activeIsolationAncestorDepthById: ctx.activeIsolationAncestorDepthById,
    editScopeGroupId: ctx.editScopeGroupId,
    elementZIndexMap: ctx.elementZIndexMap,
    isInShapeCreationMode: ctx.isInShapeCreationMode,
    scale: ctx.scale,
    shapeBounds: ctx.shapeBounds,
    textOnPathPreview: ctx.textOnPathPreview,
    editingShapeTextId: ctx.editingShapeTextId,
    shapeTextEditorRef: ctx.shapeTextEditorRef,
    shapeTextWrapContext: ctx.shapeTextWrapContext,
    shapeTextCaretClientPoint: ctx.shapeTextCaretClientPoint,
    shapeTextCaretOffset: ctx.shapeTextCaretOffset,
    suppressShapeTextExitOnBlur: ctx.suppressShapeTextExitOnBlur,
    liveChains: ctx.liveChains,
    chainFrameIdsByShapeId: ctx.chainFrameIdsByShapeId,
    chainSelectionRangeByShapeId: ctx.chainSelectionRangeByShapeId,
    chainTextSelection: ctx.chainTextSelection,
  }
  const layerHandlers = useStableCallbacks<ShapeLayerHandlers>({
    onTextOnPathLengthChange: ctx.onTextOnPathLengthChange,
    onTextOnPathStartDistanceChange: ctx.onTextOnPathStartDistanceChange,
    handleShapeClickInternal: ctx.handleShapeClickInternal,
    handleShapeDoubleClickInternal: ctx.handleShapeDoubleClickInternal,
    handleShapeContextMenuInternal: ctx.handleShapeContextMenuInternal,
    handleBoundsChange: ctx.handleBoundsChange,
    handleShapeTextParagraphsChange: ctx.handleShapeTextParagraphsChange,
    exitShapeTextEdit: ctx.exitShapeTextEdit,
    onShapeTextContextMenu: ctx.onShapeTextContextMenu,
    handleShapeTextOverflowChange: ctx.handleShapeTextOverflowChange,
    setChainTextSelection: ctx.setChainTextSelection,
    execChainClipboard: ctx.execChainClipboard,
    replaceChainTextSelection: ctx.replaceChainTextSelection,
    extendChainTextSelection: ctx.extendChainTextSelection,
    onSelectShapeTextChain: ctx.onSelectShapeTextChain,
    onShapeTextChainBackspaceAtStart: ctx.onShapeTextChainBackspaceAtStart,
    onShapeTextChainArrowLeftAtStart: ctx.onShapeTextChainArrowLeftAtStart,
    onShapeTextChainArrowRightAtEnd: ctx.onShapeTextChainArrowRightAtEnd,
    onShapeTextChainOverflowCaret: ctx.onShapeTextChainOverflowCaret,
  })

  return (
    <div
      ref={ctx.containerRef}
      className={ctx.className}
      onPointerDown={ctx.handleCanvasPointerDown}
    >
      {ctx.shapesForPaintOrder.map((shape) => (
        <ShapeLayerItem
          key={shape.id}
          shape={shape}
          presentation={shapeLayerPresentation(shape, layerState)}
          text={shapeLayerText(shape, layerState)}
          handlers={layerHandlers}
        />
      ))}

      {/* Multi-select: individual shape highlight outlines for a unified (mixed shape+text)
          selection. The visual layer draws its own screen-space chrome for a shapes-only
          multi-selection; it suppresses itself here because `center-canvas` owns the frame for a
          unified selection instead, so this in-canvas version survives only for that one case.

          This `<div>` lives inside the zoom-scaled layer, so its stroke has to be divided by
          `scale` to hold a constant screen size the way `HANDLE_BORDER_WIDTH` does everywhere
          else. It did not, and a text frame reaches this path (a text frame IS a shape, so any
          mixed selection containing one lands here) — that is WMPCM-9868's "text border changes
          size when zooming". */}
      {ctx.suppressMultiSelectChrome &&
        !ctx.isInNodeEditMode &&
        (ctx.isMultiSelect || ctx.isSingleSelect) && (
          <SelectionOutlineHighlights
            selectedShapes={ctx.selectedShapes}
            shapeBounds={ctx.shapeBounds}
            scale={ctx.scale}
            selectionOutlineDocWidth={ctx.selectionOutlineDocWidth}
            multiSelectionStrokeColor={ctx.multiSelectionStrokeColor}
            isLockedMultiSelected={ctx.isLockedMultiSelected}
            isFrozenOnlyMultiSelected={ctx.isFrozenOnlyMultiSelected}
            onUnlockSelectionBadgeClick={ctx.onUnlockSelectionBadgeClick}
            onUnfreezeSelectionBadgeClick={ctx.onUnfreezeSelectionBadgeClick}
          />
        )}
    </div>
  )
})

ShapeManagerInner.displayName = 'ShapeManager'

/** Memoized so a canvas render that changes none of its props skips the whole shape layer. */
const ShapeManager = memo(ShapeManagerInner)

export default ShapeManager
export type { ShapeManagerProps }
